import React from 'react';
import {
  Avatar,
  createTheme,
  Box,
  Card,
  Button,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Tooltip,
  Chip,
  FormControlLabel
} from '@mui/material';

import AutorenewIcon from '@mui/icons-material/Autorenew';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';

import {
  format,
  endOfMonth,
  isValid,
} from 'date-fns';

import Client from '../Client';
import BackofficeReportsApi from './backofficeReportsApi';
import Divider from '@mui/material/Divider';

let theme = createTheme();

function isValidDateString(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T00:00:00`);
  if (!isValid(d)) return false;
  return format(d, 'yyyy-MM-dd') === value;
}

function isValidMonthString(value) {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}-01T00:00:00`);
  if (!isValid(d)) return false;
  return format(d, 'yyyy-MM') === value;
}

function addDays(date, days) {
  if (!isValidDateString(date)) return "";
  const result = new Date(`${date}T00:00:00`);
  result.setDate(result.getDate() + days);
  if (!isValid(result)) return "";
  return format(result, 'yyyy-MM-dd');
}

function monthToFirstDay(monthStr) {
  if (!isValidMonthString(monthStr)) return "";
  return `${monthStr}-01`;
}

function monthToLastDay(monthStr) {
  if (!isValidMonthString(monthStr)) return "";
  const firstDay = new Date(`${monthStr}-01T00:00:00`);
  if (!isValid(firstDay)) return "";
  return format(endOfMonth(firstDay), 'yyyy-MM-dd');
}

function formatRoDate(value) {
  if (!value) return "";
  const d = new Date(Date.parse(value));
  if (!isValid(d)) return "";
  return d.toLocaleDateString('ro-RO');
}

let d = new Date();
let today = format(d, 'yyyy-MM-dd');
let currentMonth = format(d, 'yyyy-MM');

const objectToCsv = function (data) {
  if (!data || !data.length) return "";

  const csvRows = [];
  const headers = [
    ...(data[0].date ? ["date"] : data[0].month ? ["month"] : []),
    ...Object.keys(data[0]).filter(k => k !== "date" && k !== "month")
  ];

  csvRows.push(headers.join(','));

  for (const row of data) {
    const values = headers.map(header => {
      const val = row[header] !== undefined && row[header] !== null ? row[header] : "";
      return `"${String(val).replace(/"/g, '""')}"`;
    });

    csvRows.push(values.join(','));
  }

  return csvRows.join('\n');
};

class SalesExport extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),

      // modal
      generatedFilesOpen: false,

      // results
      saleslocation_list: [],
      sales_list: null,
      sales_list_month: null,
      salesproduct_list: null,
      salesproduct_list_month: null,

      sx_hidden: { display: 'none' },
      sx_hiddenP: { display: 'none' },
      sx_hiddenM: { display: 'none' },
      sx_hiddenPM: { display: 'none' },

      busyOverlay: false,

      // generated backend CSV files
      csvDownloadP: null,
      csvDownloadPM: null,
      generatedFiles: [],

      // date mode
      dateFrom: today,
      dateTo: today,
      dateFromP: today,
      dateToP: today,
      deliverySplit: false,
      reportKind: 'product', // 'product' | 'company'

      // month mode
      monthFrom: currentMonth,
      monthTo: currentMonth,
      monthFromP: currentMonth,
      monthToP: currentMonth,

      // UI
      modeLocation: 'month', // 'date' | 'month'
      modeProduct: 'month',  // 'date' | 'month'
      dates: "",
      datesP: "",
      months: "",
      monthsP: "",
      dateError: "",
      productDateError: "",
      locationMonthError: "",
      productMonthError: "",
    };

    this.getData = this.getData.bind(this);
    this.getDataP = this.getDataP.bind(this);
    this.getDataM = this.getDataM.bind(this);
    this.getDataPM = this.getDataPM.bind(this);

    this.addDateFrom = this.addDateFrom.bind(this);
    this.addDateTo = this.addDateTo.bind(this);
    this.addDateFromP = this.addDateFromP.bind(this);
    this.addDateToP = this.addDateToP.bind(this);

    this.addMonthFrom = this.addMonthFrom.bind(this);
    this.addMonthTo = this.addMonthTo.bind(this);
    this.addMonthFromP = this.addMonthFromP.bind(this);
    this.addMonthToP = this.addMonthToP.bind(this);

    this.onModeLocation = this.onModeLocation.bind(this);
    this.onModeProduct = this.onModeProduct.bind(this);
    this.onDeliverySplit = this.onDeliverySplit.bind(this);

    this.queryData = {};
  }

  componentDidMount() {
    // Keep only the lighter automatic report.
    this.getDataM();

    // Do not auto-generate product CSV export on Dashboard open.
    // this.getDataPM();

    // Optional: this works only if backend endpoint exists.
    // this.refreshGeneratedFiles();
  }

  // ------------------------------------------------------
  // Backend-generated CSV helpers
  // ------------------------------------------------------

  openGeneratedFiles = () => {
    this.setState({ generatedFilesOpen: true }, () => {
      this.refreshGeneratedFiles();
    });
  };

  closeGeneratedFiles = () => {
    this.setState({ generatedFilesOpen: false });
  };

  getDownloadUrlFromFileName = (fileName) => {
    if (!fileName) return "";

    return BackofficeReportsApi.getExportsDownloadUrl(fileName);
  };

  normalizeGeneratedFile = (data) => {
    if (!data) return null;

    const fileName = data.fileName || data.file || data.filename || "";

    if (!fileName) return null;

    return {
      fileName: fileName,
      downloadUrl: this.getDownloadUrlFromFileName(fileName),
      start: data.start || "",
      end: data.end || "",
      createdOn: data.createdOn || data.generatedOn || new Date().toLocaleString('ro-RO'),
      size: data.size || data.sizeBytes || null,
      source: data.source || "Product Sales",
    };
  };

  addGeneratedFileToState = (data) => {
    const file = this.normalizeGeneratedFile(data);
    if (!file) return;

    this.setState((prev) => {
      const existing = prev.generatedFiles || [];
      const withoutDuplicate = existing.filter(x => x.fileName !== file.fileName);

      return {
        generatedFiles: [file, ...withoutDuplicate].slice(0, 10)
      };
    });
  };

  getDownloadAuthToken = () => {
    const rawApiUser = localStorage.getItem("apiUser");
    if (rawApiUser) {
      try {
        const apiUser = JSON.parse(rawApiUser);
        if (apiUser.accessToken) {
          return apiUser.accessToken;
        }
        if (apiUser.access) {
          return apiUser.access;
        }
        if (apiUser.token) {
          return apiUser.token;
        }
      } catch (e) {
        return "";
      }
    }
    return "";
  };

  downloadFileFromUrl = async (downloadInfo) => {
    if (!downloadInfo) return;
    const file = this.normalizeGeneratedFile(downloadInfo);
    if (!file || !file.downloadUrl) return;

    try {
      const token = this.getDownloadAuthToken();
      if (!token) {
        alert("Download failed because the session token was not found. Please login again.");
        return;
      }

      const response = await fetch(file.downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      });

      if (!response.ok) {
        console.error("Download failed:", response.status, response.statusText);
        if (response.status === 401 || response.status === 403) {
          alert("Download failed because the session could not be authenticated. Please refresh the page and try again.");
        } else {
          alert("Download failed. Please try again.");
        }
        return;
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = file.fileName || "sales_export.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Download error:", error);
      alert("Download failed. Please try again.");
    }
  };

  refreshGeneratedFiles = () => {
    try {
      const data = BackofficeReportsApi.getExportsList(this.state.client, { limit: 10 });

      if (!data) return;

      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data.result)
          ? data.result
          : [];

      const files = rows
        .map(this.normalizeGeneratedFile)
        .filter(Boolean)
        .slice(0, 10);

      if (files.length) {
        this.setState({ generatedFiles: files });
      }
    } catch (e) {
      // Optional endpoint. Ignore if not available.
    }
  };

  // ------------------------------------------------------
  // Data fetches
  // ------------------------------------------------------

  setReportError = (key, message) => {
    this.setState({
      [key]: message,
      busyOverlay: false
    });
  };

  validateDateRange = (from, to, errorKey) => {
    if (!isValidDateString(from) || !isValidDateString(to)) {
      this.setReportError(errorKey, "Invalid date. Please select a valid date.");
      return false;
    }

    if (new Date(`${to}T00:00:00`) < new Date(`${from}T00:00:00`)) {
      this.setReportError(errorKey, "End date cannot be before start date.");
      return false;
    }

    this.setState({ [errorKey]: "" });
    return true;
  };

  validateMonthRange = (from, to, errorKey) => {
    if (!isValidMonthString(from) || !isValidMonthString(to)) {
      this.setReportError(errorKey, "Invalid month. Please select a valid month.");
      return false;
    }

    if (new Date(`${to}-01T00:00:00`) < new Date(`${from}-01T00:00:00`)) {
      this.setReportError(errorKey, "End month cannot be before start month.");
      return false;
    }

    this.setState({ [errorKey]: "" });
    return true;
  };

  getData() {
    if (!this.validateDateRange(this.state.dateFrom, this.state.dateTo, "dateError")) {
      return;
    }

    this.setState({ busyOverlay: true }, () => {
      setTimeout(() => {
        try {
          let queryData = {
            "dateFrom": this.state.dateFrom,
            "dateTo": this.state.dateTo
          };

          let data = BackofficeReportsApi.postExportSalesLocationsByDate(this.state.client, queryData);

          if (data) {
            this.setState({
              sales_list: data,
              dates: formatRoDate(data.start) + "-" + formatRoDate(data.end),
              sx_hidden: (data.result && data.result.length > 0) ? {} : { display: 'none' },
              filterData: queryData
            });
          }
        } finally {
          this.setState({ busyOverlay: false });
        }
      }, 0);
    });
  }

  getDataP() {
    if (!this.validateDateRange(this.state.dateFromP, this.state.dateToP, "productDateError")) {
      return;
    }

    this.setState({ busyOverlay: true }, () => {
      setTimeout(() => {
        try {
          let queryData = {
            "dateFrom": this.state.dateFromP,
            "dateTo": this.state.dateToP,
            "groupByDeliveryType": this.state.reportKind === 'company' ? false : this.state.deliverySplit
          };

          let data = this.state.reportKind === 'company'
            ? BackofficeReportsApi.postExportCompanySalesDetail(this.state.client, queryData)
            : this.state.deliverySplit
              ? BackofficeReportsApi.postExportSalesProductsDelivery(this.state.client, queryData)
              : BackofficeReportsApi.postExportSalesProductsByDate(this.state.client, queryData);

          // New backend-generated CSV flow.
          if (data && data.downloadUrl) {
            this.addGeneratedFileToState({
              ...data,
              source: this.state.reportKind === 'company' ? "Company Sales" : "Product Sales"
            });

            this.setState({
              csvDownloadP: data,
              salesproduct_list: data,
              datesP: formatRoDate(data.start) + "-" + formatRoDate(data.end),
              sx_hiddenP: {},
              filterData: queryData,
              activeTab: 0
            });

            return;
          }

          // Old JSON flow.
          if (data) {
            this.setState({
              salesproduct_list: data,
              csvDownloadP: null,
              datesP: formatRoDate(data.start) + "-" + formatRoDate(data.end),
              sx_hiddenP: (data.result && data.result.length > 0) ? {} : { display: 'none' },
              filterData: queryData
            });
          }
        } finally {
          this.setState({ busyOverlay: false });
        }
      }, 0);
    });
  }

  getDataM() {
    if (!this.validateMonthRange(this.state.monthFrom, this.state.monthTo, "locationMonthError")) {
      return;
    }

    this.setState({ busyOverlay: true }, () => {
      setTimeout(() => {
        try {
          const mFrom = monthToFirstDay(this.state.monthFrom);
          const mTo = monthToLastDay(this.state.monthTo);

          if (!mFrom || !mTo) {
            this.setReportError("locationMonthError", "Invalid month range.");
            return;
          }

          let queryData = {
            "dateFrom": mFrom,
            "dateTo": mTo
          };

          let data = BackofficeReportsApi.postExportSalesLocationsByMonth(this.state.client, queryData);

          if (data) {
            this.setState({
              sales_list_month: data,
              months: formatRoDate(mFrom) + "-" + formatRoDate(mTo),
              sx_hiddenM: (data.result && data.result.length > 0) ? {} : { display: 'none' },
              filterData: queryData
            });
          }
        } finally {
          this.setState({ busyOverlay: false });
        }
      }, 0);
    });
  }

  getDataPM() {
    if (!this.validateMonthRange(this.state.monthFromP, this.state.monthToP, "productMonthError")) {
      return;
    }

    this.setState({ busyOverlay: true }, () => {
      setTimeout(() => {
        try {
          const mFrom = monthToFirstDay(this.state.monthFromP);
          const mTo = monthToLastDay(this.state.monthToP);

          if (!mFrom || !mTo) {
            this.setReportError("productMonthError", "Invalid month range.");
            return;
          }

          let queryData = {
            "dateFrom": mFrom,
            "dateTo": mTo,
            "groupByDeliveryType": this.state.reportKind === 'company' ? false : this.state.deliverySplit
          };

          let data = this.state.reportKind === 'company'
            ? BackofficeReportsApi.postExportCompanySalesDetail(this.state.client, queryData)
            : this.state.deliverySplit
              ? BackofficeReportsApi.postExportSalesProductsDelivery(this.state.client, queryData)
              : BackofficeReportsApi.postExportSalesProductsByMonth(this.state.client, queryData);

          // New backend-generated CSV flow.
          if (data && data.downloadUrl) {
            this.addGeneratedFileToState({
              ...data,
              source: this.state.reportKind === 'company' ? "Company Sales Monthly" : "Product Sales Monthly"
            });

            this.setState({
              csvDownloadPM: data,
              salesproduct_list_month: data,
              monthsP: formatRoDate(data.start || mFrom) + "-" + formatRoDate(data.end || mTo),
              sx_hiddenPM: {},
              filterData: queryData,
              activeTab: 0
            });

            return;
          }

          // Old JSON flow.
          if (data) {
            this.setState({
              salesproduct_list_month: data,
              csvDownloadPM: null,
              monthsP: formatRoDate(data.start || mFrom) + "-" + formatRoDate(data.end || mTo),
              sx_hiddenPM: (data.result && data.result.length > 0) ? {} : { display: 'none' },
              filterData: queryData
            });
          }
        } finally {
          this.setState({ busyOverlay: false });
        }
      }, 0);
    });
  }

  // ------------------------------------------------------
  // Handlers
  // ------------------------------------------------------

  onModeLocation(_, value) {
    if (!value) return;

    this.setState({ modeLocation: value }, () => {
      if (value === 'date') this.getData();
      if (value === 'month') this.getDataM();
    });
  }

  onModeProduct(_, value) {
    if (!value) return;

    this.setState({ modeProduct: value }, () => {
      if (value === 'date') this.getDataP();
      if (value === 'month') this.getDataPM();
    });
  }

  onDeliverySplit(event) {
    const checked = event.target.checked;

    this.setState({
      deliverySplit: checked,
      csvDownloadP: null,
      csvDownloadPM: null,
      sx_hiddenP: { display: 'none' },
      sx_hiddenPM: { display: 'none' }
    });
  }

  addDateFrom = (event) => {
    this.setState({
      dateFrom: event.target.value,
      dateTo: event.target.value,
      dateError: ""
    });
  };

  addDateTo = (event) => {
    this.setState({ dateTo: event.target.value, dateError: "" });
  };

  addDateFromP = (event) => {
    this.setState({
      dateFromP: event.target.value,
      dateToP: event.target.value,
      csvDownloadP: null,
      sx_hiddenP: { display: 'none' },
      productDateError: ""
    });
  };

  addDateToP = (event) => {
    this.setState({
      dateToP: event.target.value,
      csvDownloadP: null,
      sx_hiddenP: { display: 'none' },
      productDateError: ""
    });
  };

  addMonthFrom = (event) => {
    this.setState({
      monthFrom: event.target.value,
      monthTo: event.target.value,
      locationMonthError: ""
    });
  };

  addMonthTo = (event) => {
    this.setState({ monthTo: event.target.value, locationMonthError: "" });
  };

  addMonthFromP = (event) => {
    this.setState({
      monthFromP: event.target.value,
      monthToP: event.target.value,
      csvDownloadPM: null,
      sx_hiddenPM: { display: 'none' },
      productMonthError: ""
    });
  };

  addMonthToP = (event) => {
    this.setState({
      monthToP: event.target.value,
      csvDownloadPM: null,
      sx_hiddenPM: { display: 'none' },
      productMonthError: ""
    });
  };

  // ------------------------------------------------------
  // Downloads
  // ------------------------------------------------------

  onDownload = () => {
    const rows = (this.state.sales_list && this.state.sales_list.result) ? this.state.sales_list.result : [];
    if (!rows.length) return;

    const link = document.createElement("a");
    link.download = "locationSales_" + this.state.dateFrom + "_" + this.state.dateTo + ".csv";

    let csvData = objectToCsv(rows);
    let csvHeader = "Location Sales\nDate From: " + this.state.dateFrom + "\nDate To: " + this.state.dateTo + "\n";
    let csv = "\uFEFF" + csvHeader + "\n" + csvData;

    link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    link.click();
  };

  onDownloadP = () => {
    if (this.state.csvDownloadP && this.state.csvDownloadP.downloadUrl) {
      this.downloadFileFromUrl(this.state.csvDownloadP);
      return;
    }

    const rows = (this.state.salesproduct_list && this.state.salesproduct_list.result) ? this.state.salesproduct_list.result : [];
    if (!rows.length) return;

    const link = document.createElement("a");
    link.download = (this.state.reportKind === 'company' ? "companySales_" : "productSales_") + this.state.dateFromP + "_" + this.state.dateToP + ".csv";

    let csvData = objectToCsv(rows);
    let csvHeader =
      (this.state.reportKind === 'company' ? "Company Sales Detail" : "Product Sales") +
      "\nDate From: " + this.state.dateFromP +
      "\nDate To: " + this.state.dateToP + "\n";

    let csv = "\uFEFF" + csvHeader + "\n" + csvData;

    link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    link.click();
  };

  onDownloadM = () => {
    const rows = (this.state.sales_list_month && this.state.sales_list_month.result) ? this.state.sales_list_month.result : [];
    if (!rows.length) return;

    const link = document.createElement("a");
    link.download = "locationSales_month_" + this.state.monthFrom + "_" + this.state.monthTo + ".csv";

    let csvData = objectToCsv(rows);
    let csvHeader = "Location Sales (Monthly)\nMonth From: " + this.state.monthFrom + "\nMonth To: " + this.state.monthTo + "\n";
    let csv = "\uFEFF" + csvHeader + "\n" + csvData;

    link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    link.click();
  };

  onDownloadPM = () => {
    if (this.state.csvDownloadPM && this.state.csvDownloadPM.downloadUrl) {
      this.downloadFileFromUrl(this.state.csvDownloadPM);
      return;
    }

    const rows = (this.state.salesproduct_list_month && this.state.salesproduct_list_month.result) ? this.state.salesproduct_list_month.result : [];
    if (!rows.length) return;

    const link = document.createElement("a");
    link.download = (this.state.reportKind === 'company' ? "companySales_month_" : "productSales_month_") + this.state.monthFromP + "_" + this.state.monthToP + ".csv";

    let csvData = objectToCsv(rows);
    let csvHeader =
      (this.state.reportKind === 'company' ? "Company Sales Detail (Monthly)" : "Product Sales (Monthly)") +
      "\nMonth From: " + this.state.monthFromP +
      "\nMonth To: " + this.state.monthToP + "\n";

    let csv = "\uFEFF" + csvHeader + "\n" + csvData;

    link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    link.click();
  };

  // ------------------------------------------------------
  // Render sections
  // ------------------------------------------------------

  renderGeneratedFilesDialog() {
    const files = this.state.generatedFiles || [];

    return (
      <Dialog
        open={this.state.generatedFilesOpen}
        onClose={this.closeGeneratedFiles}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            width: {
              xs: 'calc(100% - 24px)',
              sm: 'calc(100% - 64px)'
            },
            maxHeight: {
              xs: '86vh',
              sm: '82vh'
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Generated files
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last generated CSV exports. Files are temporary and may expire after 24 hours.
            </Typography>
          </Box>

          <IconButton size="small" onClick={this.closeGeneratedFiles}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: { xs: 1.5, sm: 2.5 },
            py: 2,
            bgcolor: 'rgba(0,0,0,0.015)'
          }}
        >
          {!files.length ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.paper'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No generated files yet. Prepare an export first, then it will appear here.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1.25}>
              {files.slice(0, 10).map((file, index) => (
                <Paper
                  key={file.fileName || index}
                  variant="outlined"
                  sx={{
                    p: { xs: 1.25, sm: 1.5 },
                    borderRadius: 2,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 1.5,
                      flexDirection: { xs: 'column', sm: 'row' }
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Tooltip title={file.fileName || ""}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {file.fileName}
                        </Typography>
                      </Tooltip>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          flexWrap: 'wrap',
                          mt: 0.75
                        }}
                      >
                        <Chip
                          size="small"
                          label={file.source || "CSV"}
                          variant="outlined"
                        />

                        {(file.start || file.end) && (
                          <Typography variant="caption" color="text.secondary">
                            {file.start || ""} - {file.end || ""}
                          </Typography>
                        )}

                        {file.createdOn && (
                          <Typography variant="caption" color="text.secondary">
                            • {file.createdOn}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => this.downloadFileFromUrl(file)}
                      startIcon={<FileDownloadIcon />}
                      sx={{
                        alignSelf: { xs: 'stretch', sm: 'center' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Download
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 1.5, sm: 2.5 },
            py: 1.25,
            justifyContent: 'space-between'
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={this.refreshGeneratedFiles}
            startIcon={<AutorenewIcon />}
          >
            Refresh
          </Button>

          <Button
            size="small"
            onClick={this.closeGeneratedFiles}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderReports() {
    const isLocMonth = this.state.modeLocation === 'month';
    const isProdMonth = this.state.modeProduct === 'month';

    const sectionSx = {
      borderRadius: '14px',
      border: '1px solid rgba(15, 23, 42, 0.06)',
      boxShadow: 'none',
      overflow: 'hidden',
      bgcolor: '#fff',
    };

    const sectionHeaderSx = {
      px: { xs: 1, sm: 1.25 },
      py: { xs: 0.85, sm: 1 },
      borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
      bgcolor: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    };

    const sectionBodySx = {
      p: { xs: 1, sm: 1.25 },
    };

    const fieldSx = {
      width: {
        xs: 'calc(50% - 6px)',
        sm: 170,
      },
      '& .MuiInputBase-root': {
        height: 38,
        borderRadius: '12px',
        fontSize: { xs: '0.82rem', sm: '0.88rem' },
        bgcolor: '#fff',
      },
      '& .MuiInputLabel-root': {
        fontSize: { xs: '0.78rem', sm: '0.84rem' },
      },
    };

    const dateFieldSx = {
      width: {
        xs: 'calc(50% - 6px)',
        sm: 140,
      },
      '& .MuiInputBase-root': {
        height: 38,
        borderRadius: '12px',
        fontSize: { xs: '0.82rem', sm: '0.88rem' },
        bgcolor: '#fff',
      },
      '& .MuiInputLabel-root': {
        fontSize: { xs: '0.78rem', sm: '0.84rem' },
      },
    };

    const toggleSx = {
      '& .MuiToggleButtonGroup-root': {
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: '#fff',
      },
      '& .MuiToggleButton-root': {
        height: 36,
        px: { xs: 1, sm: 1.25 },
        textTransform: 'none',
        fontWeight: 700,
        fontSize: { xs: '0.78rem', sm: '0.84rem' },
        color: '#64748b',
        borderColor: 'rgba(15, 23, 42, 0.10)',
      },
      '& .MuiToggleButton-root.Mui-selected': {
        color: '#1a237e',
        bgcolor: 'rgba(26, 35, 126, 0.08)',
      },
    };

    const actionButtonSx = {
      height: 38,
      minWidth: 38,
      borderRadius: '12px',
      boxShadow: 'none',
      textTransform: 'none',
      fontWeight: 750,
      px: { xs: 1, sm: 1.25 },
      '&:hover': {
        boxShadow: 'none',
      },
    };

    const periodRowSx = {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mt: 1,
      flexWrap: 'wrap',
    };

    const periodTextSx = {
      color: '#1a237e',
      fontWeight: 650,
      fontSize: { xs: '0.78rem', sm: '0.86rem' },
      lineHeight: 1.25,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: 0,
    };

    return (
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          p: { xs: 0.75, sm: 1 },
          pb: { xs: 3, sm: 1 },
          bgcolor: '#fff',
          '&::-webkit-scrollbar': {
            height: 8,
            width: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(15, 23, 42, 0.18)',
            borderRadius: 999,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(15, 23, 42, 0.04)',
          },
        }}
      >
        <Stack spacing={1}>
          <Paper sx={sectionSx}>
            <Box sx={sectionHeaderSx}>
              <Typography
                sx={{
                  color: '#1a237e',
                  fontWeight: 800,
                  fontSize: { xs: '0.92rem', sm: '1rem' },
                  lineHeight: 1.2,
                }}
              >
                Location Sales
              </Typography>
              <Typography
                sx={{
                  mt: 0.15,
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: { xs: '0.72rem', sm: '0.78rem' },
                  lineHeight: 1.2,
                }}
              >
                Export sales by location for a selected date or month range
              </Typography>
            </Box>

            <Box sx={sectionBodySx}>
              <Box sx={{ ...toggleSx, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  size="small"
                  value={this.state.modeLocation}
                  exclusive
                  onChange={this.onModeLocation}
                >
                  <ToggleButton value="month">Month</ToggleButton>
                  <ToggleButton value="date">Date</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {isLocMonth ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <TextField
                      size="small"
                      label="Month From"
                      type="month"
                      value={this.state.monthFrom}
                      sx={fieldSx}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: "2020-01", max: currentMonth }}
                      onChange={this.addMonthFrom}
                    />

                    <TextField
                      size="small"
                      label="Month To"
                      type="month"
                      value={this.state.monthTo}
                      sx={fieldSx}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: this.state.monthFrom || "2020-01", max: currentMonth }}
                      onChange={this.addMonthTo}
                    />

                    <Tooltip title="Refresh report">
                      <Button
                        onClick={() => this.getDataM()}
                        variant="contained"
                        sx={actionButtonSx}
                      >
                        <AutorenewIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </Box>

                  <Box sx={periodRowSx}>
                    <Typography sx={periodTextSx}>
                      Period: {this.state.months || '—'}
                    </Typography>

                    <Button
                      sx={{
                        ...this.state.sx_hiddenM,
                        height: 36,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 750,
                      }}
                      size="small"
                      onClick={this.onDownloadM}
                      variant="contained"
                      color="success"
                      startIcon={<FileDownloadIcon fontSize="small" />}
                    >
                      CSV
                    </Button>
                  </Box>

                  {this.state.locationMonthError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {this.state.locationMonthError}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <TextField
                      size="small"
                      label="From"
                      type="date"
                      value={this.state.dateFrom}
                      sx={dateFieldSx}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: "2020-01-01", max: today }}
                      onChange={this.addDateFrom}
                    />

                    <TextField
                      size="small"
                      label="To"
                      type="date"
                      value={this.state.dateTo}
                      sx={dateFieldSx}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: this.state.dateFrom || "2020-01-01", max: addDays(this.state.dateFrom, 50) || today }}
                      onChange={this.addDateTo}
                    />

                    <Tooltip title="Refresh report">
                      <Button
                        onClick={() => this.getData()}
                        variant="contained"
                        sx={actionButtonSx}
                      >
                        <AutorenewIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </Box>

                  <Box sx={periodRowSx}>
                    <Typography sx={periodTextSx}>
                      Period: {this.state.dates || '—'}
                    </Typography>

                    <Button
                      sx={{
                        ...this.state.sx_hidden,
                        height: 36,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 750,
                      }}
                      size="small"
                      onClick={this.onDownload}
                      variant="contained"
                      color="success"
                      startIcon={<FileDownloadIcon fontSize="small" />}
                    >
                      CSV
                    </Button>
                  </Box>

                  {this.state.dateError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {this.state.dateError}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Paper>

          <Paper sx={sectionSx}>
            <Box sx={sectionHeaderSx}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 1,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: '#1a237e',
                      fontWeight: 800,
                      fontSize: { xs: '0.92rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    Product / Company Sales
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.15,
                      color: '#64748b',
                      fontWeight: 600,
                      fontSize: { xs: '0.72rem', sm: '0.78rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    Prepare product or company sales CSV exports
                  </Typography>
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={this.openGeneratedFiles}
                  startIcon={<FileDownloadIcon fontSize="small" />}
                  sx={{
                    height: 36,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 750,
                    whiteSpace: 'nowrap',
                    alignSelf: { xs: 'stretch', sm: 'center' },
                  }}
                >
                  Generated files
                </Button>
              </Box>
            </Box>

            <Box sx={sectionBodySx}>
              <Box
                sx={{
                  ...toggleSx,
                  display: 'flex',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 0.75, sm: 1 },
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.75, sm: 1 },
                    flexWrap: 'wrap',
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <ToggleButtonGroup
                    size="small"
                    value={this.state.modeProduct}
                    exclusive
                    onChange={this.onModeProduct}
                  >
                    <ToggleButton value="month">Month</ToggleButton>
                    <ToggleButton value="date">Date</ToggleButton>
                  </ToggleButtonGroup>

                  <ToggleButtonGroup
                    size="small"
                    value={this.state.reportKind}
                    exclusive
                    onChange={(_, value) => {
                      if (!value) return;

                      this.setState({
                        reportKind: value,
                        csvDownloadP: null,
                        csvDownloadPM: null,
                        sx_hiddenP: { display: 'none' },
                        sx_hiddenPM: { display: 'none' },
                        productDateError: "",
                        productMonthError: ""
                      });
                    }}
                  >
                    <ToggleButton value="product">Product</ToggleButton>
                    <ToggleButton value="company">Company</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {this.state.reportKind === 'product' && (
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={this.state.deliverySplit}
                        onChange={this.onDeliverySplit}
                      />
                    }
                    label="Delivery"
                    sx={{
                      m: 0,
                      width: { xs: '100%', sm: 'auto' },
                      justifyContent: { xs: 'space-between', sm: 'flex-start' },
                      px: { xs: 1, sm: 0 },
                      py: { xs: 0.25, sm: 0 },
                      borderRadius: { xs: '12px', sm: 0 },
                      bgcolor: { xs: 'rgba(15, 23, 42, 0.03)', sm: 'transparent' },
                      '& .MuiFormControlLabel-label': {
                        fontSize: { xs: '0.78rem', sm: '0.84rem' },
                        color: '#64748b',
                        fontWeight: 650,
                      },
                    }}
                  />
                )}
              </Box>

              {this.state.deliverySplit && this.state.reportKind === 'product' && (
                <Box
                  sx={{
                    mt: 1,
                    px: 1,
                    py: 0.75,
                    borderRadius: '12px',
                    bgcolor: 'rgba(26, 35, 126, 0.06)',
                    border: '1px solid rgba(26, 35, 126, 0.10)',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontWeight: 600,
                      fontSize: {
                        xs: '0.7rem',
                        sm: '0.78rem',
                      },
                      lineHeight: 1.25,
                    }}
                  >
                    Delivery split prepares a server CSV file. Refresh first, then download.
                  </Typography>
                </Box>
              )}

              {isProdMonth ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <TextField
                      size="small"
                      label="Month From"
                      type="month"
                      value={this.state.monthFromP}
                      sx={fieldSx}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: "2020-01", max: currentMonth }}
                      onChange={this.addMonthFromP}
                    />

                    <TextField
                      size="small"
                      label="Month To"
                      type="month"
                      value={this.state.monthToP}
                      sx={fieldSx}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: this.state.monthFromP || "2020-01", max: currentMonth }}
                      onChange={this.addMonthToP}
                    />

                    <Tooltip title="Prepare export">
                      <Button
                        onClick={() => this.getDataPM()}
                        variant="contained"
                        sx={actionButtonSx}
                      >
                        <AutorenewIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </Box>

                  <Box sx={periodRowSx}>
                    <Typography sx={periodTextSx}>
                      Period: {this.state.monthsP || '—'}
                    </Typography>

                    <Button
                      sx={{
                        ...this.state.sx_hiddenPM,
                        height: 36,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 750,
                      }}
                      size="small"
                      onClick={this.onDownloadPM}
                      variant="contained"
                      color="success"
                      startIcon={<FileDownloadIcon fontSize="small" />}
                    >
                      CSV
                    </Button>
                  </Box>

                  {this.state.productMonthError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {this.state.productMonthError}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <TextField
                      size="small"
                      label="From"
                      type="date"
                      value={this.state.dateFromP}
                      sx={dateFieldSx}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: "2020-01-01", max: today }}
                      onChange={this.addDateFromP}
                    />

                    <TextField
                      size="small"
                      label="To"
                      type="date"
                      value={this.state.dateToP}
                      sx={dateFieldSx}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: this.state.dateFromP || "2020-01-01", max: addDays(this.state.dateFromP, 50) || today }}
                      onChange={this.addDateToP}
                    />

                    <Tooltip title="Prepare export">
                      <Button
                        onClick={() => this.getDataP()}
                        variant="contained"
                        sx={actionButtonSx}
                      >
                        <AutorenewIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </Box>

                  <Box sx={periodRowSx}>
                    <Typography sx={periodTextSx}>
                      Period: {this.state.datesP || '—'}
                    </Typography>

                    <Button
                      sx={{
                        ...this.state.sx_hiddenP,
                        height: 36,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 750,
                      }}
                      size="small"
                      onClick={this.onDownloadP}
                      variant="contained"
                      color="success"
                      startIcon={<FileDownloadIcon fontSize="small" />}
                    >
                      CSV
                    </Button>
                  </Box>

                  {this.state.productDateError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {this.state.productDateError}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Stack>
      </Box>
    );
  }

  render() {
    return (
      <Card
        variant="outlined"
        sx={{
          position: 'relative',
          width: '100%',
          height: {
            xs: 600,
            sm: '100%',
          },
          maxHeight: {
            xs: 600,
            sm: '100%',
          },
          minHeight: 0,
          borderRadius: {
            xs: '14px',
            sm: '16px',
          },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          bgcolor: '#fff',
        }}
      >
        <Box
          sx={{
            px: { xs: 0.75, sm: 1 },
            py: { xs: 0.65, sm: 0.85 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexShrink: 0,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: '#1a237e',
                fontWeight: 800,
                fontSize: {
                  xs: '0.9rem',
                  sm: '1rem',
                },
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Sales Export
            </Typography>

            <Typography
              sx={{
                mt: 0.15,
                color: '#64748b',
                fontWeight: 600,
                fontSize: {
                  xs: '0.72rem',
                  sm: '0.78rem',
                },
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Prepare location, product, and company CSV reports
            </Typography>
          </Box>

          <Chip
            size="small"
            label="CSV"
            sx={{
              flexShrink: 0,
              color: '#1a237e',
              bgcolor: 'rgba(26, 35, 126, 0.08)',
              border: '1px solid rgba(26, 35, 126, 0.12)',
              borderRadius: 999,
              fontWeight: 800,
            }}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {this.renderReports()}
        </Box>

        {this.renderGeneratedFilesDialog()}

        {this.state.busyOverlay && (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(15, 23, 42, 0.16)',
              backgroundImage:
                'radial-gradient(ellipse at center, rgba(15, 23, 42, 0.08) 0%, rgba(15, 23, 42, 0.18) 75%, rgba(15, 23, 42, 0.22) 100%)',
              zIndex: 9999,
              pointerEvents: 'auto',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                px: 2.5,
                py: 1.5,
                borderRadius: '14px',
                bgcolor: 'background.paper',
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid rgba(15, 23, 42, 0.08)',
              }}
            >
              <CircularProgress size={22} color="inherit" />
              <Typography variant="body2">Preparing report…</Typography>
            </Paper>
          </Box>
        )}
      </Card>
    );
  }
}

export default SalesExport;
